import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  SchemeContent,
  SchemeKnob,
  SchemeKnobValues,
  SchemeManifest,
} from "@shared/types/foyer";
import type { TemplateSubSection } from "../PremiseSidebar";
import { tokens } from "../../theme";
import { EDITOR_MAX_WIDTH } from "./editorLayout";
import {
  buildScreenGroups,
  moveZone,
  reorder,
  reorderTo,
  resolveOrders,
  screenSummary,
  type ScreenGroup,
  type ScreenRow,
} from "./screensModel";

interface Props {
  manifest: SchemeManifest;
  value: SchemeKnobValues | undefined;
  content?: SchemeContent;
  onChange: (next: SchemeKnobValues) => void;
  /** Jump to the Component tab that edits a screen's content. */
  onOpenSub?: (sub: TemplateSubSection) => void;
  /**
   * Does this premise hold an active Promotions license? Defaults to `true`
   * (licensed) so every existing caller that predates this gate keeps
   * behaving the same. The real editor always passes the premise's actual
   * answer - see `hasPromotionsLicense` (`@shared/products`).
   */
  hasPromotions?: boolean;
  /**
   * Is a certified app switched on for this premise, fixing part of the
   * launcher arrangement (NFLX-4)? Defaults to `false` - a caller that never
   * asked must not find switches missing. `TemplateEditor` passes the real
   * answer.
   */
  appsCertified?: boolean;
  /** What requires it, shown in place of that row's switch. */
  certificationNote?: string;
}

/**
 * Template → Screens: the one place that answers "what does this TV show".
 *
 * Replaces the old Toggles page, which put every switch behind a collapsed
 * accordion and left the matching content editor in a separate part of the nav
 * (that vanished when you switched the screen off). Here a screen is one card,
 * built on the same anatomy as the Information directory's section cards: a
 * tinted strip carrying its state and the reorder arrows, then the screen's
 * name, what it says, and a link into its own editor.
 *
 * A card can also be MOVED between the two zones - onto the home screen, or
 * down under Information - because placement is membership of the two order
 * knobs (`homeOrder` / `infoOrder`). Three paths do the same thing on purpose:
 * drag with a pointer, drag with the keyboard (dnd-kit's own sensor), and the
 * explicit "Move to …" button. Drag alone would have made a per-premise TV
 * layout decision reachable only with a mouse.
 */
export default function ScreensEditor({
  manifest,
  value,
  content,
  onChange,
  onOpenSub,
  hasPromotions = true,
  appsCertified = false,
  certificationNote,
}: Props) {
  const raw = value ?? {};
  const set = (id: string, v: boolean | string | string[]) =>
    onChange({ ...raw, [id]: v });
  const groups = buildScreenGroups(manifest, value, {
    hasPromotions,
    appsCertified,
    certificationNote,
  });
  const [dragging, setDragging] = useState<string | null>(null);

  const byKey = useMemo(() => {
    const m = new Map<string, { row: ScreenRow; group: ScreenGroup }>();
    for (const group of groups) {
      for (const row of group.rows) m.set(row.key, { row, group });
    }
    return m;
  }, [groups]);

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so the switch, the arrows
    // and "Edit content" stay ordinary click targets on the card.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (groups.length === 0) return null;

  // Placement is read from ALL the order knobs at once - see `resolveOrders`.
  const resolved = resolveOrders(manifest, value);
  const orderOf = (knobId: string) => resolved[knobId] ?? [];

  const move = (orderKnobId: string, rowKey: string, dir: -1 | 1) => {
    const next = reorder(orderOf(orderKnobId), rowKey, dir);
    if (next) set(orderKnobId, next);
  };

  /** The explicit, keyboard-reachable version of dragging across the divide. */
  const moveToZone = (row: ScreenRow, index?: number) => {
    if (!row.moveTo) return;
    const patch = moveZone(
      manifest,
      value,
      row.key,
      row.moveTo.orderKnobId,
      index
    );
    if (patch) onChange(patch);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(null);
    if (!over) return;
    const from = byKey.get(String(active.id));
    if (!from?.row.orderKnobId) return;

    // `over` is either another card or the zone itself (dropped in the gap
    // below the last card) - the latter means "append to that zone".
    const overRow = byKey.get(String(over.id));
    const target =
      overRow?.group ?? groups.find((g) => g.orderKnobId === over.id);
    if (!target?.orderKnobId) return;

    if (target.orderKnobId === from.row.orderKnobId) {
      const order = orderOf(target.orderKnobId);
      const at = overRow ? order.indexOf(overRow.row.key) : order.length;
      const next = reorderTo(order, from.row.key, at < 0 ? order.length : at);
      if (next) set(target.orderKnobId, next);
      return;
    }
    // Crossing zones. A screen the manifest declares in only one order knob has
    // no `moveTo`, so it snaps back rather than landing somewhere the TV would
    // have to invent copy and a Back destination for.
    if (from.row.moveTo?.orderKnobId !== target.orderKnobId) return;
    const order = orderOf(target.orderKnobId);
    const at = overRow ? order.indexOf(overRow.row.key) : order.length;
    moveToZone(from.row, at < 0 ? undefined : at);
  };

  const activeRow = dragging ? byKey.get(dragging)?.row : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }: DragStartEvent) =>
        setDragging(String(active.id))
      }
      onDragCancel={() => setDragging(null)}
      onDragEnd={onDragEnd}
    >
      <Stack
        spacing={4}
        sx={{ maxWidth: EDITOR_MAX_WIDTH }}
        data-testid="screens-editor"
      >
        {groups.map((group) => {
          const orderable = group.rows.filter((r) => r.orderable);
          const shown = group.rows.filter((r) => r.on).length;
          return (
            <Box key={group.title}>
              <Typography variant="h6">
                {group.title} ({shown} shown)
              </Typography>
              {group.hint && (
                <Typography
                  sx={{ fontSize: 12.5, color: tokens.inkFaint, mt: 0.25 }}
                >
                  {group.hint}
                </Typography>
              )}

              <Zone
                droppableId={group.orderKnobId}
                items={orderable.map((r) => r.key)}
              >
                {group.rows.map((row) => (
                  <SortableScreenCard
                    key={row.key}
                    row={row}
                    content={content}
                    canMoveUp={row.orderable && orderable.indexOf(row) > 0}
                    canMoveDown={
                      row.orderable &&
                      orderable.indexOf(row) < orderable.length - 1
                    }
                    onToggle={(on) => row.knobId && set(row.knobId, on)}
                    onTitle={
                      row.title ? (v) => set(row.title!.knobId, v) : undefined
                    }
                    onMove={
                      group.orderKnobId
                        ? (dir) => move(group.orderKnobId!, row.key, dir)
                        : undefined
                    }
                    onMoveZone={row.moveTo ? () => moveToZone(row) : undefined}
                    onOpen={
                      row.sub && onOpenSub
                        ? () => onOpenSub(row.sub!)
                        : undefined
                    }
                  />
                ))}
              </Zone>

              {group.extras.length > 0 && (
                <Stack spacing={2} sx={{ mt: 2, maxWidth: 360 }}>
                  {group.extras.map((knob) => (
                    <ExtraKnob
                      key={knob.id}
                      knob={knob}
                      raw={raw}
                      onSet={(v) => set(knob.id, v)}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* The card follows the cursor at full size - a thin ghost line would not
          say which of eleven near-identical cards is in flight. */}
      <DragOverlay>
        {activeRow ? (
          <ScreenCard
            row={activeRow}
            content={content}
            canMoveUp={false}
            canMoveDown={false}
            onToggle={() => {}}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * One zone's card grid: a sortable context plus a drop target of its own, so
 * a zone with no cards left in it can still be dropped into.
 *
 * Two columns, `rectSortingStrategy` rather than the old single-file
 * `verticalListSortingStrategy` - a card can now be dropped into any
 * position in the grid, not just above/below its neighbours, so the drag
 * needs to reason in two dimensions.
 *
 * `alignItems: "stretch"` (the grid default, left implicit) pairs the two
 * cards in a row to the same height. Cards carry a wildly variable amount of
 * body - Screen Share is a name and one line of help, Apps adds a summary
 * ("12 apps") and an "Edit content" button - and letting each card size to its
 * own content produced ragged, mismatched pairs down the whole page. The
 * shorter card absorbs the difference as space above its footer, which is
 * pinned to the bottom by `mt: "auto"` in {@link ScreenCard}, so "Edit content"
 * lands on one baseline across the row instead of floating mid-card.
 */
function Zone({
  droppableId,
  items,
  children,
}: {
  droppableId?: string;
  items: string[];
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? "__unsortable__",
    disabled: !droppableId,
  });
  return (
    <SortableContext items={items} strategy={rectSortingStrategy}>
      <Box
        ref={setNodeRef}
        sx={{
          mt: 2,
          minHeight: 64,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: "12px",
          borderRadius: "16px",
          outline: isOver && droppableId ? "2px dashed" : "none",
          outlineColor: tokens.brand,
          outlineOffset: 6,
          transition: "outline-color 120ms ease",
        }}
      >
        {children}
      </Box>
    </SortableContext>
  );
}

type CardProps = {
  row: ScreenRow;
  content?: SchemeContent;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: (on: boolean) => void;
  /** Rename this screen. Only a screen the operator names has one. */
  onTitle?: (value: string) => void;
  onMove?: (dir: -1 | 1) => void;
  onMoveZone?: () => void;
  onOpen?: () => void;
};

/**
 * The card as it lives in a zone. Kept separate from {@link ScreenCard} so the
 * DragOverlay copy - which renders OUTSIDE the SortableContext - never calls
 * `useSortable` and never registers a second draggable under the same id.
 */
function SortableScreenCard(props: CardProps) {
  const sortable = useSortable({
    id: props.row.key,
    disabled: !props.row.orderable,
  });
  return (
    <ScreenCard
      {...props}
      setNodeRef={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        // The original stays in place as a hole while its overlay flies, so the
        // list never collapses and re-flows under the cursor.
        opacity: sortable.isDragging ? 0.35 : undefined,
      }}
      dragHandle={
        props.row.orderable
          ? { ...sortable.attributes, ...sortable.listeners }
          : undefined
      }
    />
  );
}

function ScreenCard({
  row,
  content,
  canMoveUp,
  canMoveDown,
  onToggle,
  onTitle,
  onMove,
  onMoveZone,
  onOpen,
  setNodeRef,
  style,
  dragHandle,
}: CardProps & {
  setNodeRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  /** Sortable attributes+listeners for the grab handle. Absent = not draggable
   *  (a pinned screen, or the overlay copy). */
  dragHandle?: Record<string, unknown>;
}) {
  // The summary shows even when the screen is off: the content still exists,
  // and "you already wrote 3 weeks of menu" is exactly what makes an operator
  // realise the screen is the thing that's missing, not the text.
  // Fall back to the row key: Offers has no editor tab (its catalogue lives
  // under Frontlayer → Offers) but still deserves its one-line summary.
  const summary = screenSummary(row.sub ?? row.key, content);
  const switchable = !!row.knobId;
  const state = row.fixedNote ? "Always on" : row.on ? "On" : "Off";

  return (
    <Box
      ref={setNodeRef}
      data-testid={`screen-row-${row.key}`}
      style={style}
      sx={{
        bgcolor: tokens.surface,
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        boxShadow: tokens.shadowCard,
        overflow: "hidden",
        // Stretched to its row's tallest card by the grid; the column lets the
        // body below claim that extra height instead of leaving it under the
        // card. Harmless in the DragOverlay, which sizes to the card's own rect.
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.75,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          bgcolor: tokens.rowAlt,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {dragHandle && (
            <Tooltip
              title={
                row.moveTo
                  ? `Drag to reorder, or into ${row.moveTo.title}`
                  : "Drag to reorder"
              }
            >
              <IconButton
                size="small"
                aria-label={`Drag ${row.label}`}
                data-testid={`screen-drag-${row.key}`}
                sx={{ cursor: "grab", color: tokens.inkFaint, ml: -0.5 }}
                {...dragHandle}
              >
                <DragIndicatorIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {switchable ? (
            <Switch
              size="small"
              checked={row.on}
              onChange={(e) => onToggle(e.target.checked)}
              inputProps={{ "aria-label": `Show ${row.label}` }}
            />
          ) : null}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".09em",
              textTransform: "uppercase",
              color: row.on ? tokens.brand : tokens.inkFaint,
            }}
          >
            {state}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          {onMoveZone && row.moveTo && (
            <Button
              size="small"
              startIcon={
                row.moveTo.title === "Inside Information" ? (
                  <MoveDownIcon fontSize="inherit" />
                ) : (
                  <MoveUpIcon fontSize="inherit" />
                )
              }
              onClick={onMoveZone}
              aria-label={`Move ${row.label} to ${row.moveTo.title}`}
              data-testid={`screen-move-${row.key}`}
              sx={{ fontSize: 11.5, flex: "none" }}
            >
              {row.moveTo.title === "Inside Information"
                ? "Move to Information"
                : "Move to Home screen"}
            </Button>
          )}
          {onMove && row.orderable && (
            <>
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${row.label} up`}
                    disabled={!canMoveUp}
                    onClick={() => onMove(-1)}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${row.label} down`}
                    disabled={!canMoveDown}
                    onClick={() => onMove(1)}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 2.25,
          opacity: row.on ? 1 : 0.6,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: tokens.ink }}>
            {row.label}
          </Typography>
          {(row.fixedNote || row.help) && (
            <Typography
              sx={{ fontSize: 12.5, lineHeight: 1.5, color: tokens.inkMuted }}
            >
              {row.fixedNote ?? row.help}
            </Typography>
          )}
          {/* A screen the operator names carries its name field here rather than
              in a separate settings page: the card already IS the screen, and the
              name is the one thing about it that isn't content. */}
          {row.title && onTitle && (
            <TextField
              size="small"
              label={row.title.label}
              placeholder={row.title.placeholder}
              helperText={row.title.help}
              value={row.title.raw}
              onChange={(e) => onTitle(e.target.value)}
              inputProps={{
                maxLength: row.title.maxLength,
                "aria-label": `${row.title.label} - ${row.label}`,
              }}
              data-testid={`screen-title-${row.key}`}
              sx={{ maxWidth: 320 }}
            />
          )}
        </Stack>
        {/* `mt: "auto"` is what turns the stretched height into a shared
            baseline: the slack a short card inherits from its taller neighbour
            opens ABOVE this row, never between the name and its help text. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: "auto", pt: 1 }}
        >
          <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
            {summary ?? ""}
          </Typography>
          {onOpen && (
            <Button
              size="small"
              endIcon={<ChevronRightIcon />}
              onClick={onOpen}
              data-testid={`screen-edit-${row.key}`}
              sx={{ flex: "none" }}
            >
              Edit content
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

/** Knobs that aren't screens (layout variants). Rendered plainly, below. */
function ExtraKnob({
  knob,
  raw,
  onSet,
}: {
  knob: SchemeKnob;
  raw: SchemeKnobValues;
  onSet: (v: string) => void;
}) {
  if (knob.type !== "variant") return null;
  const v = raw[knob.id];
  const value =
    typeof v === "string" && knob.options.some((o) => o.id === v)
      ? v
      : knob.default;
  return (
    <TextField
      size="small"
      select
      fullWidth
      label={knob.label}
      helperText={knob.help}
      value={value}
      onChange={(e) => onSet(e.target.value)}
    >
      {knob.options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
